import { z } from 'zod';
import { assistantCardSchema } from './assistant.js';
import { todayCardSchema } from './today.js';
/**
 * Tela grande (TV, notebook): uma página pareada por código que mostra o que o
 * Secretário está fazendo e os resultados em layouts de sala. O celular é o
 * microfone e o cérebro; o servidor repassa as cenas pelos eventos da tela.
 */
export const SCREEN_CODE_LENGTH = 6;
export const screenSchema = z.object({
    id: z.uuid(),
    name: z.string().max(60),
    /** Rótulo do aparelho (navegador), para a pessoa reconhecer a tela. */
    label: z.string().max(80),
    pairedAt: z.iso.datetime().nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    /** A tela está aberta agora (recebendo eventos). */
    connected: z.boolean(),
    /** É a tela que recebe o que o Secretário mostra neste momento. */
    active: z.boolean(),
});
export const screenClaimRequestSchema = z.object({
    label: z.string().trim().max(80).optional(),
    /** Segredo da tela guardado no navegador dela: a mesma tela volta sem novo código. */
    token: z.string().trim().min(16).max(80).optional(),
});
export const screenClaimResultSchema = z.object({
    id: z.uuid(),
    token: z.string(),
    /** Código de pareamento (só enquanto ninguém a ligou a uma conta). */
    code: z.string().length(SCREEN_CODE_LENGTH).nullable(),
    paired: z.boolean(),
    name: z.string().max(60).nullable(),
});
/** Tela aberta pelo próprio celular: já pareada, sem código. */
export const screenSelfRequestSchema = z.object({ name: z.string().trim().max(60).optional() });
export const screenSelfResultSchema = z.object({
    screen: screenSchema,
    token: z.string().min(10).max(80),
});
export const screenPairRequestSchema = z.object({
    code: z
        .string()
        .trim()
        .transform((value) => value.replace(/\D/g, ''))
        .pipe(z.string().length(SCREEN_CODE_LENGTH)),
    name: z.string().trim().min(1).max(60).optional(),
});
/** Etapa do que ele está fazendo, como na trilha do celular. */
export const screenStepSchema = z.object({
    id: z.string().max(80),
    tool: z.string().max(60),
    label: z.string().max(80),
    detail: z.string().max(140).nullable().optional(),
    state: z.enum(['running', 'done', 'failed']),
});
/** Relatório visual: o que o modelo monta a partir da pesquisa, pronto para a tela desenhar. */
export const screenReportSchema = z.object({
    titulo: z.string().max(120),
    resumo: z.string().max(600),
    grafico: z
        .object({
        tipo: z.enum(['barras', 'pizza', 'linhas']),
        titulo: z.string().max(120),
        unidade: z.string().max(30).default(''),
        itens: z
            .array(z.object({
            nome: z.string().max(60),
            valor: z.number().finite(),
            nota: z.string().max(200).optional(),
        }))
            .min(2)
            .max(10),
    })
        .nullable(),
    secoes: z.array(z.object({ titulo: z.string().max(80), texto: z.string().max(700) })).max(6),
    fontes: z.array(z.object({ titulo: z.string().max(160), url: z.string().max(400) })).max(8),
});
/**
 * Apresentação em movimento: uma aula, um pitch, um treinamento, montados por um
 * só pedido ao modelo forte e desenhados pela tela a partir de templates fixos
 * (capa, tópicos, passos, comparação, número, gráfico, citação, fim). Sem LLM por
 * slide: barato, rápido e sempre com a mesma cara.
 */
export const DECK_SLIDE_KINDS = [
    'capa',
    'secao',
    'topicos',
    'passos',
    'comparacao',
    'numero',
    'kpis',
    'grafico',
    'tabela',
    'linha_do_tempo',
    'imagem',
    'video',
    'citacao',
    'fim',
];
/**
 * Temas visuais da casa para apresentações: o modelo escolhe o tema, nunca o layout.
 * Cada tema é um conjunto fixo de cores e tipos desenhado por nós (fase 2 do estudo).
 */
export const DECK_THEMES = ['executivo', 'sobrio', 'aula', 'vibrante', 'minimalista'];
/** Vídeo do YouTube (id de 11 caracteres) com o título para o botão de abrir fora. */
export const screenVideoSchema = z.object({
    youtube: z.string().regex(/^[A-Za-z0-9_-]{11}$/),
    titulo: z.string().max(140),
    canal: z.string().max(80).optional(),
    inicio: z.number().int().min(0).max(36_000).optional(),
});
/** Imagem guardada na tela (gerada ou do Documentos), servida por um endereço com vale. */
export const screenImageSchema = z.object({
    asset: z.string().max(80),
    alt: z.string().max(200),
});
export const screenDeckSlideSchema = z.object({
    tipo: z.enum(DECK_SLIDE_KINDS),
    titulo: z.string().max(120),
    subtitulo: z.string().max(220).optional(),
    /** Tópicos ou passos (até 6). */
    itens: z.array(z.string().max(170)).max(6).optional(),
    /** Comparação: duas colunas. */
    colunas: z
        .array(z.object({ titulo: z.string().max(60), itens: z.array(z.string().max(130)).max(5) }))
        .max(2)
        .optional(),
    /** Número grande com rótulo. */
    numero: z.object({ valor: z.string().max(24), rotulo: z.string().max(140) }).optional(),
    grafico: z
        .object({
        tipo: z.enum(['barras', 'pizza', 'linhas']),
        titulo: z.string().max(120),
        unidade: z.string().max(30).default(''),
        itens: z
            .array(z.object({ nome: z.string().max(60), valor: z.number().finite() }))
            .min(2)
            .max(8),
    })
        .optional(),
    citacao: z
        .object({ texto: z.string().max(320), autor: z.string().max(80).optional() })
        .optional(),
    /** Vários números lado a lado (2 a 4). */
    kpis: z
        .array(z.object({
        valor: z.string().max(24),
        rotulo: z.string().max(80),
        tom: z.enum(['neutro', 'bom', 'alerta']).optional(),
    }))
        .max(4)
        .optional(),
    tabela: z
        .object({
        colunas: z.array(z.string().max(40)).min(2).max(5),
        linhas: z
            .array(z.array(z.string().max(60)).max(5))
            .min(1)
            .max(7),
    })
        .optional(),
    linha: z
        .array(z.object({ quando: z.string().max(30), titulo: z.string().max(90) }))
        .max(6)
        .optional(),
    /** Pedido de imagem (o servidor gera e troca por `imagem`). */
    imagem_pedido: z.string().max(300).optional(),
    imagem: screenImageSchema.optional(),
    /** Busca do vídeo (o servidor procura e troca por `video`). */
    video_busca: z.string().max(160).optional(),
    video: screenVideoSchema.optional(),
    /** O que o Secretário diz ao mostrar este slide (1 a 3 frases). */
    fala: z.string().max(420).optional(),
});
export const screenDeckSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(200).optional(),
    publico: z.string().max(80).optional(),
    tema: z.enum(DECK_THEMES).optional(),
    slides: z.array(screenDeckSlideSchema).min(3).max(14),
    fontes: z.array(z.object({ titulo: z.string().max(160), url: z.string().max(400) })).max(6),
});
/** Lista para a tela grande (notícias, itens, resultados): título, linha de apoio, fonte e hora. */
export const screenListSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(200).optional(),
    itens: z
        .array(z.object({
        titulo: z.string().max(200),
        detalhe: z.string().max(300).optional(),
        fonte: z.string().max(80).optional(),
        hora: z.string().max(40).optional(),
    }))
        .min(1)
        .max(10),
});
/**
 * Formatos que a tela sabe desenhar. `loading` desenha o mesmo formato vazio, com o
 * título já à vista e o resto pulsando, enquanto o conteúdo é montado: quem pediu vê
 * a resposta começar na hora.
 */
export const SCREEN_FORMATS = [
    'card',
    'list',
    'report',
    'deck',
    'today',
    'money',
    'table',
    'dashboard',
    'howto',
    'timeline',
    'mindmap',
    'quiz',
    'video',
    'doc',
    'library',
    'web',
    'body',
];
const tone = z.enum(['neutro', 'bom', 'alerta']).optional();
/** Tabela: comparar opções, uma planilha guardada, um levantamento. */
export const screenTableSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(220).optional(),
    colunas: z.array(z.string().max(48)).min(2).max(8),
    linhas: z
        .array(z.array(z.string().max(120)).max(8))
        .min(1)
        .max(40),
    /** Coluna em destaque (0 = primeira), por exemplo a do total ou a recomendada. */
    destaque: z.number().int().min(0).max(7).optional(),
    nota: z.string().max(300).optional(),
});
/** Painel de números: indicadores no alto, gráfico embaixo. */
export const screenDashboardSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(220).optional(),
    indicadores: z
        .array(z.object({
        rotulo: z.string().max(60),
        valor: z.string().max(24),
        variacao: z.string().max(24).optional(),
        tom: tone,
    }))
        .min(1)
        .max(6),
    grafico: screenReportSchema.shape.grafico.optional(),
    nota: z.string().max(300).optional(),
});
/** Passo a passo: receita, tutorial, procedimento; o passo atual em destaque. */
export const screenHowtoSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(220).optional(),
    ingredientes: z.array(z.string().max(120)).max(30).optional(),
    passos: z
        .array(z.object({
        titulo: z.string().max(140),
        texto: z.string().max(400).optional(),
        minutos: z.number().int().min(0).max(600).optional(),
    }))
        .min(1)
        .max(20),
    atual: z.number().int().min(0).default(0),
});
/** Linha do tempo: roteiro, cronograma, histórico. */
export const screenTimelineSchema = z.object({
    titulo: z.string().max(120),
    subtitulo: z.string().max(220).optional(),
    itens: z
        .array(z.object({
        quando: z.string().max(40),
        titulo: z.string().max(120),
        texto: z.string().max(300).optional(),
        tom: tone,
    }))
        .min(2)
        .max(12),
});
/** Mapa mental: um centro e ramos com itens. */
export const screenMindmapSchema = z.object({
    titulo: z.string().max(120),
    centro: z.string().max(60),
    ramos: z
        .array(z.object({ titulo: z.string().max(50), itens: z.array(z.string().max(70)).max(5) }))
        .min(2)
        .max(7),
});
/** Quiz de aula: a tela mostra, a turma responde, o controle revela. */
export const screenQuizSchema = z.object({
    titulo: z.string().max(120),
    perguntas: z
        .array(z.object({
        pergunta: z.string().max(240),
        opcoes: z.array(z.string().max(120)).min(2).max(4),
        certa: z.number().int().min(0).max(3),
        explicacao: z.string().max(300).optional(),
    }))
        .min(1)
        .max(12),
});
/** Documento aberto na tela: foto, PDF, texto ou documento pronto. */
export const screenDocSchema = z.object({
    titulo: z.string().max(160),
    /** `apresentacao`: PowerPoint guardado, convertido em páginas (uma por slide). */
    tipo: z.enum(['imagem', 'pdf', 'texto', 'documento', 'apresentacao']),
    /** Imagem, PDF e apresentação: o arquivo pelo vale da tela. */
    asset: z.string().max(80).optional(),
    texto: z.string().max(60_000).optional(),
    /** Documento pronto (o mesmo dos Documentos), desenhado em blocos. */
    documento: z.unknown().optional(),
    origem: z.string().max(120).optional(),
});
/** Biblioteca: o que ele tem guardado para abrir na tela com o controle. */
/** De onde vem cada item da biblioteca da tela (as mesmas seções dos Documentos do app). */
export const LIBRARY_SOURCES = [
    'guardado',
    'documento',
    'receita',
    'ata',
    'lista',
    'tela',
];
export const screenLibrarySchema = z.object({
    titulo: z.string().max(120),
    itens: z
        .array(z.object({
        id: z.string().max(80),
        fonte: z.enum(LIBRARY_SOURCES),
        titulo: z.string().max(160),
        tipo: z.string().max(40),
        quando: z.string().max(40).optional(),
    }))
        .max(40),
});
/**
 * Página de um site liberado, aberta na tela dentro de uma moldura trancada: só o
 * domínio liberado carrega lá dentro (nem redirecionamento sai dele), sem janelas,
 * formulários, câmera ou microfone. A moldura vem do servidor pelo vale da tela e
 * recebe só o domínio; o endereço completo (que pode ser a chave de um relatório)
 * segue depois do `#`, que o navegador não manda a servidor nenhum.
 */
export const screenWebSchema = z.object({
    /** Domínio liberado que aparece no topo ("galileuonline.com.br"). */
    site: z.string().max(253),
    endereco: z.url().max(2000),
    titulo: z.string().max(120),
});
/**
 * Cores das faixas de um medidor de saúde. São as mesmas do sistema de origem (vermelho
 * longe do esperado, verde na meta, azul acima, magenta da gordura essencial); a tela
 * só suaviza o tom para a identidade dela.
 */
export const BODY_TONES = [
    'vermelho',
    'laranja',
    'ambar',
    'amarelo',
    'lima',
    'verde_claro',
    'verde',
    'menta',
    'azul',
    'azul_forte',
    'magenta',
    'rosa',
    'rosa_claro',
];
/** Medidor em arco: a escala e as faixas do relatório de origem, ponto por ponto. */
export const bodyGaugeSchema = z.object({
    min: z.number(),
    max: z.number(),
    /** Onde o ponteiro para (o valor do exame). */
    valor: z.number(),
    escala: z.enum(['linear', 'log']),
    /** Faixas em ordem: cada uma vai do fim da anterior (ou de `min`) até `ate`. */
    faixas: z
        .array(z.object({ ate: z.number(), tom: z.enum(BODY_TONES) }))
        .min(1)
        .max(12),
    /** Marcas da escala, com o texto já formatado. */
    marcas: z.array(z.object({ em: z.number(), texto: z.string().max(12) })).max(12),
    /** O número no centro do arco, formatado como no relatório ("9,1%"). */
    centro: z.string().max(16),
});
/** Uma medida do exame, já formatada como no relatório de origem. */
export const bodyMetricSchema = z.object({
    rotulo: z.string().max(48),
    valor: z.string().max(24),
    unidade: z.string().max(32).optional(),
    /** Segundo valor da mesma medida ("68,1 %" junto de "48,4 litros"). */
    extra: z.string().max(24).optional(),
    /** Diferença para o exame anterior ("+0,4"), quando o relatório compara. */
    diferenca: z.string().max(12).optional(),
});
export const bodyPanelSchema = z.object({
    id: z.enum(['gordura', 'hidratacao', 'agua', 'massa', 'medidas', 'celular']),
    titulo: z.string().max(48),
    medidor: bodyGaugeSchema.optional(),
    /** Nome da faixa em que o valor caiu, quando o relatório de origem dá nome a ela. */
    faixa: z.object({ nome: z.string().max(40), tom: z.enum(BODY_TONES) }).optional(),
    medidas: z.array(bodyMetricSchema).max(6),
    /** Água: intracelular e extracelular em proporção (somam 100). */
    divisao: z
        .object({
        dentro: z.object({ rotulo: z.string().max(32), percentual: z.number(), texto: z.string() }),
        fora: z.object({ rotulo: z.string().max(32), percentual: z.number(), texto: z.string() }),
    })
        .optional(),
});
/**
 * Relatório de bioimpedância de um sistema parceiro (hoje o b.IA do Galileu), redesenhado
 * na identidade do Secretário. Todo número vem do relatório de origem, formatado como lá;
 * nada é estimado, interpretado ou completado aqui.
 */
export const screenBodySchema = z.object({
    titulo: z.string().max(120),
    pessoa: z.object({
        nome: z.string().max(120),
        /** "40 anos · masculino" */
        resumo: z.string().max(80),
        /** Escolhe o desenho do corpo (sem ele, o masculino). */
        sexo: z.enum(['M', 'F']).optional(),
    }),
    /** "07/06/2025 às 10:24" (horário de Brasília). */
    quando: z.string().max(40),
    /** Clínica, avaliador e a fonte, como no cabeçalho do relatório. */
    origem: z.object({
        clinica: z.string().max(120).optional(),
        avaliador: z.string().max(120).optional(),
        sistema: z.string().max(60),
        site: z.string().max(253),
    }),
    /** Números do topo: peso, altura, idade, IMC. */
    destaques: z.array(bodyMetricSchema).max(6),
    paineis: z.array(bodyPanelSchema).max(6),
    /** Meta registrada no relatório ("Cintura de 80 cm em 3 meses"). */
    meta: z.string().max(120).optional(),
    /** "Comparado ao exame de 02/05/2025", quando há evolução. */
    comparacao: z.string().max(80).optional(),
    /** Marcas do próprio relatório (ex.: desidratação "DH"). */
    avisos: z.array(z.string().max(160)).max(4),
});
export const moneyMonthSceneSchema = z.object({
    month: z.string(),
    receivedCents: z.number().int(),
    paidCents: z.number().int(),
    leftoverCents: z.number().int(),
    savingsRate: z.number().nullable(),
});
/** O que a tela mostra agora. Uma cena de cada vez; a tela anima a troca. */
export const screenSceneSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('idle') }),
    z.object({
        kind: z.literal('steps'),
        steps: z.array(screenStepSchema).max(8),
        // Passos do celular por cima do que já está na tela (cartão, relatório): uma faixa
        // que some quando termina, sem trocar a cena. Lista vazia tira a faixa.
        overlay: z.boolean().optional(),
    }),
    z.object({ kind: z.literal('say'), text: z.string().max(600) }),
    z.object({
        kind: z.literal('card'),
        card: assistantCardSchema,
        said: z.string().max(600).optional(),
    }),
    z.object({
        kind: z.literal('today'),
        cards: z.array(todayCardSchema).max(8),
        greeting: z.string().max(120),
    }),
    z.object({ kind: z.literal('money'), months: z.array(moneyMonthSceneSchema).max(12) }),
    z.object({ kind: z.literal('report'), report: screenReportSchema }),
    z.object({
        kind: z.literal('loading'),
        format: z.enum(SCREEN_FORMATS),
        title: z.string().max(120),
    }),
    z.object({ kind: z.literal('list'), list: screenListSchema }),
    z.object({ kind: z.literal('table'), table: screenTableSchema }),
    z.object({ kind: z.literal('dashboard'), dashboard: screenDashboardSchema }),
    z.object({ kind: z.literal('howto'), howto: screenHowtoSchema }),
    z.object({ kind: z.literal('timeline'), timeline: screenTimelineSchema }),
    z.object({ kind: z.literal('mindmap'), mindmap: screenMindmapSchema }),
    z.object({ kind: z.literal('quiz'), quiz: screenQuizSchema }),
    z.object({ kind: z.literal('video'), video: screenVideoSchema }),
    z.object({ kind: z.literal('doc'), doc: screenDocSchema }),
    z.object({ kind: z.literal('library'), library: screenLibrarySchema }),
    z.object({ kind: z.literal('web'), web: screenWebSchema }),
    z.object({ kind: z.literal('body'), body: screenBodySchema }),
    z.object({
        kind: z.literal('deck'),
        deck: screenDeckSchema,
        /** Slide à vista (começa em 0). */
        slide: z.number().int().min(0),
    }),
]);
/** Eventos que a tela recebe (SSE). */
/** Teclas do controle no celular (repassadas à tela como se fossem do controle da TV). */
export const SCREEN_REMOTE_KEYS = [
    'up',
    'down',
    'left',
    'right',
    'ok',
    'back',
    /** Tocar, pausar e alternar (o botão do controle) o vídeo na tela. */
    'play',
    'pause',
    'playpause',
    'next',
    'prev',
];
export const screenRemoteKeySchema = z.object({ key: z.enum(SCREEN_REMOTE_KEYS) });
/**
 * Deslizar no controle do celular: quanto o ponteiro anda na tela, em fração da largura e
 * da altura dela (o celular soma o movimento do dedo e manda aos pedaços).
 */
export const screenRemotePointerSchema = z.object({
    dx: z.number().min(-1).max(1),
    dy: z.number().min(-1).max(1),
});
export const screenEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('hello'),
        screenName: z.string().max(60),
        ownerName: z.string().max(80),
        secretaryName: z.string().max(60),
        characterKey: z.string().max(40),
        /** A tela é a ativa agora (o Secretário está mostrando nela). */
        active: z.boolean(),
    }),
    z.object({ type: z.literal('scene'), scene: screenSceneSchema }),
    z.object({ type: z.literal('active'), active: z.boolean() }),
    z.object({ type: z.literal('unpaired') }),
    z.object({ type: z.literal('ping') }),
    /** Tecla do controle no celular: a tela age como se viesse do controle remoto. */
    z.object({ type: z.literal('key'), key: z.enum(SCREEN_REMOTE_KEYS) }),
    /** Deslizar no controle do celular: o ponteiro da tela anda. */
    z.object({
        type: z.literal('pointer'),
        dx: z.number().min(-1).max(1),
        dy: z.number().min(-1).max(1),
    }),
    /** O que já passou nesta tela (para voltar pelo controle, sem pedir de novo). */
    z.object({
        type: z.literal('history'),
        items: z
            .array(z.object({
            id: z.string().max(40),
            kind: z.string().max(20),
            title: z.string().max(160),
            at: z.string().max(40),
        }))
            .max(20),
    }),
]);
/**
 * Ações da própria tela (controle remoto, mouse, passador de slides): não passam pelo
 * modelo nem gastam pedidos. A tela se identifica pelo segredo dela.
 */
export const SCREEN_ACTIONS = [
    'inicio',
    'hoje',
    'dinheiro',
    'noticias',
    'documentos',
    'abrir',
    'historico',
    'slide',
    'passo',
    'salvar',
];
export const screenActionRequestSchema = z.object({
    token: z.string().min(10).max(80),
    action: z.enum(SCREEN_ACTIONS),
    /** abrir: id e fonte do item; historico: id; slide e passo: número (começa em 0). */
    id: z.string().max(80).optional(),
    fonte: z.enum(LIBRARY_SOURCES).optional(),
    numero: z.number().int().min(0).max(200).optional(),
    /** salvar: o formato do arquivo da apresentação ou relatório na tela. */
    /** salvar: tela (padrão, guarda como está), pptx ou documento. */
    formato: z.enum(['tela', 'pptx', 'documento']).optional(),
});
/** Ferramentas da tela grande (rodam no servidor, que fala com a tela). */
export const SCREEN_FIND_TOOL = 'procurar_tela';
export const SCREEN_BIG_SHOW_TOOL = 'conectar_tela_grande';
export const SCREEN_BIG_HIDE_TOOL = 'tirar_da_tela';
export const SCREEN_REPORT_TOOL = 'preparar_relatorio_visual';
export const SCREEN_DECK_TOOL = 'preparar_apresentacao';
export const SCREEN_DECK_CONTROL_TOOL = 'controlar_apresentacao';
/** Monta na tela um formato a partir do pedido (tabela, painel, passo a passo…). */
export const SCREEN_BUILD_TOOL = 'montar_na_tela';
/** Abre na tela um item dos Documentos (foto, PDF, documento pronto, receita). */
export const SCREEN_OPEN_TOOL = 'abrir_na_tela';
export const DECK_ACTIONS = ['proximo', 'anterior', 'inicio', 'fim', 'ir'];
/** Abre na tela um link de site liberado (ou o formato próprio de um parceiro, como o Galileu). */
export const SCREEN_LINK_TOOL = 'abrir_link_na_tela';
/**
 * Sites liberados para abrir na tela. `geral` vale para todos (lista do dono); os
 * outros, só para quem liberou. `moldura` diz se o site aceita abrir dentro de outra
 * página (conferido ao liberar; null = ainda não deu para saber).
 */
export const screenSiteSchema = z.object({
    id: z.uuid(),
    site: z.string().max(253),
    geral: z.boolean(),
    moldura: z.boolean().nullable(),
    criadoEm: z.string(),
});
export const screenSiteRequestSchema = z.object({
    /** Domínio ou um endereço do site ("galileuonline.com.br", "https://site.com/x"). */
    site: z.string().trim().min(3).max(2000),
});
/** Gera o link de um resultado (relatório no formato do Secretário) para mandar a alguém. */
export const SCREEN_SHARE_TOOL = 'compartilhar_resultado';
